import React, { useEffect, useState } from 'react';

export interface HighlightTarget {
  selector: string;
  padding?: number; // 扩展高亮区域的padding
}

export interface GuidedTourOverlayProps {
  target: HighlightTarget | null;
  visible: boolean;
  animationEnabled?: boolean;
}

/**
 * 遮罩层 + 高亮聚焦组件
 * 
 * 使用 box-shadow 技术实现半透明遮罩 + 高亮裁剪效果
 * 高亮目标元素的周围显示深色遮罩，目标元素本身高亮显示
 */
const GuidedTourOverlay: React.FC<GuidedTourOverlayProps> = ({
  target,
  visible,
  animationEnabled = true,
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isReady, setIsReady] = useState(false);

  // 获取目标元素的屏幕坐标
  useEffect(() => {
    if (!visible || !target) {
      setTargetRect(null);
      setIsReady(false);
      return;
    }

    const updateTargetRect = () => {
      const element = document.querySelector(target.selector);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        // 延迟一小段时间后设置isReady，确保动画效果
        setTimeout(() => setIsReady(true), 50);
      } else {
        setTargetRect(null);
        setIsReady(false);
      }
    };

    // 初始更新
    updateTargetRect();

    // 监听窗口大小变化
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);

    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [target, visible]);

  if (!visible) {
    return null;
  }

  const padding = target?.padding ?? 8;
  
  // 计算高亮区域的样式
  const getHighlightStyle = (): React.CSSProperties => {
    if (!targetRect) {
      return {
        display: 'none',
      };
    }

    return {
      position: 'fixed' as const,
      left: `${targetRect.left - padding}px`,
      top: `${targetRect.top - padding}px`,
      width: `${targetRect.width + padding * 2}px`,
      height: `${targetRect.height + padding * 2}px`,
      borderRadius: '8px',
      transition: animationEnabled ? 'all 0.3s ease' : 'none',
      opacity: isReady ? 1 : 0,
    };
  };

  // 如果没有目标元素，显示全屏遮罩（提示用户操作）
  if (!targetRect) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          opacity: visible ? 1 : 0,
          transition: animationEnabled ? 'opacity 0.3s ease' : 'none',
        }}
      />
    );
  }

  return (
    <>
      {/* 半透明遮罩层 */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          opacity: isReady ? 1 : 0,
          transition: animationEnabled ? 'opacity 0.3s ease' : 'none',
        }}
      />
      
      {/* 高亮聚焦框 - 使用 box-shadow 技术 */}
      <div
        style={{
          ...getHighlightStyle(),
          zIndex: 100000,
          backgroundColor: 'transparent',
          // 使用巨大的 box-shadow 创建高亮效果
          boxShadow: isReady 
            ? `0 0 0 9999px rgba(0, 0, 0, 0.5)` // 巨大的阴影覆盖整个屏幕
            : '0 0 0 0 rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none' as const, // 允许点击穿透
        }}
      />

      {/* 脉冲动画环（可选装饰） */}
      {isReady && animationEnabled && (
        <div
          style={{
            position: 'fixed',
            left: `${targetRect.left - padding - 4}px`,
            top: `${targetRect.top - padding - 4}px`,
            width: `${targetRect.width + padding * 2 + 8}px`,
            height: `${targetRect.height + padding * 2 + 8}px`,
            border: '2px solid #1890ff',
            borderRadius: '12px',
            zIndex: 100001,
            pointerEvents: 'none' as const,
            animation: 'tourHighlightPulse 2s ease-in-out infinite',
          }}
        />
      )}

      {/* 添加入困动画样式 */}
      <style>{`
        @keyframes tourHighlightPulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.02);
          }
        }
      `}</style>
    </>
  );
};

export default GuidedTourOverlay;
